import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronRight, AlertCircle } from "lucide-react";
import logo from "@/assets/logo_salone_vincente.png";
import { YouTubePlayer } from "@/components/YouTubePlayer";
import { toast } from "@/hooks/use-toast";

const NO_ACCESS_WEBHOOK =
  "https://n8n.srv1035791.hstgr.cloud/webhook/0b12a652-0f0a-4d32-b316-900fc7afbf50";

const VideoIntro = () => {
  const [showButton, setShowButton] = useState(false);
  const [step, setStep] = useState<"video" | "question" | "blocked">("video");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleTimeRemaining = (seconds: number) => {
    if (seconds <= 10 && seconds > 0 && !showButton) {
      setShowButton(true);
    }
  };

  const goToNext = () => {
    const queryString = searchParams.toString();
    navigate(`/2${queryString ? `?${queryString}` : ""}`);
  };

  const handleAnswerYes = () => {
    goToNext();
  };

  const handleAnswerNo = async () => {
    setSubmitting(true);
    const groupId = searchParams.get("groupId") || "";
    const id = searchParams.get("Id") || searchParams.get("id") || "";
    const payload = {
      risposta: "no",
      domanda: "Ti ricordi gli accessi del tuo account Facebook?",
      groupId,
      Id: id,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    };

    try {
      await fetch(NO_ACCESS_WEBHOOK, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    } catch (err) {
      console.error("Webhook error:", err);
      toast({
        title: "Errore di rete",
        description: "Non siamo riusciti a inviare la notifica. Contatta comunque il tuo consulente.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
      setStep("blocked");
    }
  };

  return (
    <div className="h-screen gradient-subtle flex flex-col p-4">
      <div className="flex-1 flex items-center justify-center">
        <div className="max-w-5xl w-full flex flex-col">
          {/* Header */}
          <div className="flex items-center gap-4 mb-3 animate-fade-in">
            <img
              src={logo}
              alt="Salone Vincente"
              className="w-16 h-16 object-contain animate-float flex-shrink-0"
            />
            <h1 className="flex-1 text-center text-xl md:text-2xl font-bold bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
              BENVENUTO NELL'ONBOARDING DI SALONE VINCENTE
            </h1>
          </div>

          {step === "video" && (
            <div className="relative card p-3 shadow-elegant animate-slide-up">
              <YouTubePlayer
                videoId="RUHTV1pBZGY"
                onTimeRemaining={handleTimeRemaining}
                showButtonAt={10}
              />

              {showButton && (
                <div className="mt-4 flex justify-center animate-fade-in">
                  <Button
                    onClick={() => setStep("question")}
                    size="lg"
                    className="bg-gradient-to-r from-primary to-secondary hover:opacity-90 transition-opacity px-8"
                  >
                    Continua al prossimo step dell'onboarding
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              )}

              {!showButton && (
                <div className="text-center mt-3 text-base md:text-lg font-semibold text-foreground animate-pulse">
                  <p>Guarda il video per continuare</p>
                </div>
              )}
            </div>
          )}

          {step === "question" && (
            <div className="card p-8 shadow-elegant animate-fade-in text-center space-y-6">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                Ti ricordi gli accessi del tuo account Facebook?
              </h2>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={handleAnswerYes}
                  disabled={submitting}
                  size="lg"
                  className="bg-gradient-to-r from-primary to-secondary hover:opacity-90 transition-opacity px-10 rounded-xl"
                >
                  Sì
                </Button>
                <Button
                  onClick={handleAnswerNo}
                  disabled={submitting}
                  size="lg"
                  variant="outline"
                  className="px-10 rounded-xl"
                >
                  {submitting ? "Invio..." : "No"}
                </Button>
              </div>
            </div>
          )}

          {step === "blocked" && (
            <div className="card p-8 shadow-elegant animate-fade-in text-center space-y-4">
              <div className="flex justify-center">
                <AlertCircle className="w-14 h-14 text-primary" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                Contatta il tuo consulente per trovare una soluzione
              </h2>
              <p className="text-muted-foreground">
                Il tuo consulente è già stato avvisato e ti aiuterà a recuperare gli accessi.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoIntro;
